import { useLocation } from 'react-router-dom';
import {
  Body1Strong,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import useItemTableStyles from '@modules/items/hooks/useItemTableStyles.hook';
import { DueDateLabel } from '@shared/components/dates/DueDateLabel';
import { SmartTableCell } from '@shared/components/tables/SmartTableCell';
import TaskAssigneeLabel from '../../tasks/components/TaskAssigneeLabel';
import TaskPriorityLabel from '../../tasks/components/TaskPriorityLabel';
import TaskStatusMenu from '../../tasks/components/TaskStatusMenu';
import { TASK_COLUMN_LABELS, TASK_COLUMN_WIDTHS } from '../../tasks/constants';
import { getTaskPath } from '../../tasks/paths';
import type { ETaskStatus, TTaskListItem } from '../../tasks/types';

type TProps = {
  projectId: string;
  tasks: TTaskListItem[];
  showAssignee: boolean;
  statusPending: boolean;
  onStatusChange: (task: TTaskListItem, status: ETaskStatus) => void;
};

const AttentionTasksTable = ({ projectId, tasks, showAssignee, statusPending, onStatusChange }: TProps) => {
  const { search } = useLocation();
  const tableClasses = useItemTableStyles();
  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>
              <Body1Strong>{TASK_COLUMN_LABELS.title}</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: `${TASK_COLUMN_WIDTHS.status}px` }}>
              <Body1Strong>{TASK_COLUMN_LABELS.status}</Body1Strong>
            </TableHeaderCell>
            {showAssignee && (
              <TableHeaderCell style={{ width: `${TASK_COLUMN_WIDTHS.assignee}px` }}>
                <Body1Strong>{TASK_COLUMN_LABELS.assignee}</Body1Strong>
              </TableHeaderCell>
            )}
            <TableHeaderCell style={{ width: `${TASK_COLUMN_WIDTHS.dueDate}px` }}>
              <Body1Strong>{TASK_COLUMN_LABELS.dueDate}</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: `${TASK_COLUMN_WIDTHS.priority}px` }}>
              <Body1Strong>{TASK_COLUMN_LABELS.priority}</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {tasks.map((task) => (
            <TableRow key={task._id}>
              <SmartTableCell
                variant={{ kind: 'link', href: getTaskPath(projectId, task._id, search), label: task.title }}
                openMode="sameTab"
                tooltip={{ threshold: 70 }}
              />
              <TableCell>
                <TaskStatusMenu
                  size="small"
                  status={task.status}
                  disabled={statusPending}
                  onStatusChange={task.permissions.canEdit ? (status) => onStatusChange(task, status) : undefined}
                />
              </TableCell>
              {showAssignee && (
                <TableCell>
                  <TaskAssigneeLabel assignee={task.assignee} />
                </TableCell>
              )}
              <TableCell>
                <DueDateLabel variant="compact" dueDate={task.dueDate} due={task.due} />
              </TableCell>
              <TableCell>
                <TaskPriorityLabel priority={task.priority} variant="compact" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};
export default AttentionTasksTable;
